import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Alert,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import { useSelector } from "react-redux";
import { Target, X, HelpCircle } from "lucide-react";

interface ICPSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    userId: string,
    icpId: string,
    modelAPIKey: string,
    additionalGenerationContext: string
  ) => void;
  error?: string;
}

const ICPSelectionDialog: React.FC<ICPSelectionDialogProps> = ({
  open,
  onClose,
  onSubmit,
  error,
}) => {
  const [modelAPIKey, setModelAPIKey] = useState("");
  const [icpId, setIcpId] = useState("");
  const [additionalGenerationContext, setAdditionalGenerationContext] =
    useState("");
  const reduxUser: any = useSelector((state: any) => state.user);

  const handleSubmit = () => {
    if (!icpId.trim()) return;
    onSubmit(
      reduxUser.id,
      icpId.trim(),
      modelAPIKey.trim(),
      additionalGenerationContext.trim()
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
          overflow: "visible",
        },
      }}
    >
      <Box sx={{ position: "absolute", top: -20, left: 24 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            backgroundColor: "#FEE2E2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 10px rgba(220, 38, 38, 0.15)",
          }}
        >
          <Target className="h-6 w-6 text-red-600" />
        </Box>
      </Box>

      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 16,
          top: 16,
          color: "#6B7280",
          backgroundColor: "#F3F4F6",
          "&:hover": {
            backgroundColor: "#E5E7EB",
          },
        }}
      >
        <X className="h-4 w-4" />
      </IconButton>

      <DialogTitle
        sx={{
          pt: 4,
          px: 4,
          fontSize: "1.25rem",
          fontWeight: 600,
          color: "#1F2937",
        }}
      >
        Enter ICP ID
      </DialogTitle>

      <DialogContent sx={{ px: 4, pt: 1 }}>
        <DialogContentText className="mb-4" sx={{ color: "#6B7280" }}>
          Please enter the ICP ID from your chosen Ideal Customer Profile. You
          can find this ID by visiting the ICP Research Agent and selecting an
          ICP from your research results. The ID will be displayed in the ICP
          details view.
        </DialogContentText>

        {error && (
          <Alert
            severity="error"
            className="mb-4"
            icon={<HelpCircle className="h-5 w-5" />}
            sx={{
              borderRadius: "8px",
              backgroundColor: "#FEE2E2",
              color: "#B91C1C",
              "& .MuiAlert-icon": {
                color: "#B91C1C",
              },
            }}
          >
            {error}
          </Alert>
        )}

        <Box
          sx={{
            mt: 3,
            p: 3,
            borderRadius: "8px",
            backgroundColor: "#F9FAFB",
            border: "1px solid #E5E7EB",
          }}
        >
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              color: "#374151",
              fontWeight: 600,
              fontSize: "0.9rem",
              mb: 2,
            }}
          >
            Model API Key
          </Typography>
          <TextField
            autoFocus
            placeholder="Enter Model API Key..."
            fullWidth
            value={modelAPIKey}
            onChange={(e) => setModelAPIKey(e.target.value)}
            variant="outlined"
            InputProps={{
              sx: {
                mb: 2,
                borderRadius: "8px",
                backgroundColor: "white",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D1D5DB",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#DC2626",
                  borderWidth: "1px",
                },
              },
            }}
          />

          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              color: "#374151",
              fontWeight: 600,
              fontSize: "0.9rem",
              mb: 2,
            }}
          >
            ICP ID
          </Typography>

          <TextField
            placeholder="Enter ICP ID..."
            fullWidth
            value={icpId}
            onChange={(e) => setIcpId(e.target.value)}
            variant="outlined"
            InputProps={{
              sx: {
                borderRadius: "8px",
                backgroundColor: "white",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D1D5DB",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#DC2626",
                  borderWidth: "1px",
                },
              },
            }}
            sx={{ mb: 2 }}
          />

          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              color: "#374151",
              fontWeight: 600,
              fontSize: "0.9rem",
              mb: 2,
            }}
          >
            Strategy Generation Context
          </Typography>

          <TextField
            placeholder="Enter any additional context for strategy generation..."
            fullWidth
            multiline
            rows={4}
            value={additionalGenerationContext}
            onChange={(e) => setAdditionalGenerationContext(e.target.value)}
            variant="outlined"
            InputProps={{
              sx: {
                borderRadius: "8px",
                backgroundColor: "white",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D1D5DB",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#DC2626",
                  borderWidth: "1px",
                },
              },
            }}
          />

          <Typography
            variant="body2"
            sx={{
              mt: 2,
              color: "#6B7280",
              fontSize: "0.8rem",
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <HelpCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Optional: Provide additional context to guide the AI in strategy
              generation. This is crucial for including information about
              previous successful or unsuccessful strategies across platforms,
              specific requirements, or industry insights that should be
              considered when creating your ad strategy.
            </span>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 4, pt: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            color: "#4B5563",
            borderRadius: "8px",
            px: 3,
            "&:hover": {
              backgroundColor: "#F3F4F6",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!icpId.trim()}
          sx={{
            background: "linear-gradient(to right, #DC2626, #EF4444)",
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "8px",
            px: 3,
            boxShadow: "0 2px 4px rgba(220, 38, 38, 0.25)",
            "&:hover": {
              background: "linear-gradient(to right, #B91C1C, #DC2626)",
              boxShadow: "0 3px 6px rgba(220, 38, 38, 0.3)",
            },
            "&.Mui-disabled": {
              background: "#F3F4F6",
              color: "#9CA3AF",
            },
          }}
        >
          Generate Ad Strategy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ICPSelectionDialog;
